import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { transactionKeys } from '@/hooks/useTransactions';
import { TransactionService } from '@/services/TransactionService';
import {
  buildAnalyticsSeries,
  resolveDisplayCurrency,
  type BurndownSeries,
} from '@/pages/analytics/budgetAnalyticsUtils';
import type { Budget } from '@/types';

export function useBudgetAnalytics(
  selectedBudgets: Budget[],
  displayCurrencyChoice: string,
  clipStart?: string,
  clipEnd?: string
) {
  const budgetIds = selectedBudgets.map((b) => b.id);

  const transactionQueries = useQueries({
    queries: budgetIds.map((id) => ({
      queryKey: transactionKeys.byBudgetTree(id, true),
      queryFn: () => TransactionService.getByBudgetTree(id, true),
      enabled: !!id,
      staleTime: 30 * 1000,
    })),
  });

  const transactionsByBudget = useMemo(() => {
    const map = new Map<string, Awaited<ReturnType<typeof TransactionService.getByBudgetTree>>>();
    budgetIds.forEach((id, index) => {
      map.set(id, transactionQueries[index]?.data ?? []);
    });
    return map;
  }, [budgetIds, transactionQueries]);

  const isLoadingTransactions =
    selectedBudgets.length > 0 && transactionQueries.some((q) => q.isLoading);

  const { currency: displayCurrency, warning: currencyWarning } = useMemo(
    () => resolveDisplayCurrency(selectedBudgets, displayCurrencyChoice),
    [selectedBudgets, displayCurrencyChoice]
  );

  const seriesQuery = useQuery({
    queryKey: [
      'analytics',
      'series',
      budgetIds,
      displayCurrency,
      clipStart ?? '',
      clipEnd ?? '',
      ...Array.from(transactionsByBudget.values()).map((txs) => txs.length),
    ],
    queryFn: () =>
      buildAnalyticsSeries(
        selectedBudgets,
        transactionsByBudget,
        displayCurrency,
        clipStart || undefined,
        clipEnd || undefined
      ),
    enabled: selectedBudgets.length > 0 && !isLoadingTransactions,
    staleTime: 60 * 1000,
  });

  return {
    series: (seriesQuery.data ?? []) as BurndownSeries[],
    displayCurrency,
    currencyWarning,
    isLoading: isLoadingTransactions || seriesQuery.isLoading,
    isError: seriesQuery.isError,
    error: seriesQuery.error,
  };
}
